import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

export interface GraphComponentsProps {
    status?: string | null;
    occupancy?: string | null;
    color?: string;
    size?: number;
}

interface StatusCircleIndicatorProps {
    status?: string | null;
    color?: string;
    size?: number;
}

interface OccupancyGlassIndicatorProps {
    occupancy?: string | null;
    color?: string;
    width?: number;
    height?: number;
}

const STROKE_COLOR = '#b8b8b8';
const EMPTY_FILL = '#ffffff';

// Theme detection is handled inline where needed.

function normalize(value?: string | null): string {
    return String(value ?? '').trim().toLowerCase();
}

function statusToSegments(status?: string | null): number {
    const normalized = normalize(status);

    // Progression can be adjusted here if business semantics change.
    if (normalized === 'started') return 1;
    if (normalized === 'in negotiation') return 2;
    if (normalized === 'sold') return 3;

    return 0;
}

function occupancyToPercent(occupancy?: string | null): number {
    const normalized = normalize(occupancy);

    if (!normalized) return 0;
    if (normalized === 'empty') return 0;
    if (normalized === 'half') return 50;
    if (normalized === 'full') return 100;

    return 0;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
    const angleRad = ((angleDeg - 90) * Math.PI) / 180;
    return {
        x: cx + r * Math.cos(angleRad),
        y: cy + r * Math.sin(angleRad),
    };
}

function createSlicePath(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
    const start = polarToCartesian(cx, cy, r, endDeg);
    const end = polarToCartesian(cx, cy, r, startDeg);
    const largeArcFlag = endDeg - startDeg <= 180 ? '0' : '1';

    return [
        `M ${cx} ${cy}`,
        `L ${start.x} ${start.y}`,
        `A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
        'Z',
    ].join(' ');
}

export function StatusCircleIndicator({
    status,
    color,
    size = 32,
}: StatusCircleIndicatorProps) {
    // Use provided color or determine from theme
    const theme = typeof window !== 'undefined' ? localStorage.getItem('theme') || 'light' : 'light';
    const finalColor = color || (theme === 'dark' ? '#c9a45c' : '#1e3a8a');
    const filled = statusToSegments(status);

    const slices = [
        { start: 0, end: 120 },
        { start: 120, end: 240 },
        { start: 240, end: 360 },
    ];

    return (
        <Box sx={{ width: size, height: size, display: 'inline-flex' }}>
            <svg width={size} height={size} viewBox='0 0 100 100' role='img' aria-label={`Status indicator: ${status ?? 'unknown'}`}>
                {slices.map((slice, index) => (
                    <path
                        key={`${slice.start}-${slice.end}`}
                        d={createSlicePath(50, 50, 45, slice.start, slice.end)}
                        fill={index < filled ? finalColor : EMPTY_FILL}
                        stroke={STROKE_COLOR}
                        strokeWidth='2'
                    />
                ))}
            </svg>
        </Box>
    );
}

export function OccupancyGlassIndicator({
    occupancy,
    color,
    width = 26,
    height = 42,
}: OccupancyGlassIndicatorProps) {
    // Use provided color or determine from theme
    const theme = typeof window !== 'undefined' ? localStorage.getItem('theme') || 'light' : 'light';
    const finalColor = color || (theme === 'dark' ? '#c9a45c' : '#1e3a8a');
    const fillPercent = occupancyToPercent(occupancy);
    const fillHeight = (height * fillPercent) / 100;

    return (
        <Box sx={{ width, height, display: 'inline-flex' }}>
            <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role='img' aria-label={`Occupancy indicator: ${occupancy ?? 'empty'}`}>
                <defs>
                    <clipPath id='occupancy-glass-clip'>
                        <rect x='2' y='2' width={width - 4} height={height - 4} rx='4' ry='4' />
                    </clipPath>
                </defs>

                <rect x='2' y='2' width={width - 4} height={height - 4} rx='4' ry='4' fill={EMPTY_FILL} stroke={STROKE_COLOR} strokeWidth='2' />

                <rect
                    x='2'
                    y={height - 2 - fillHeight}
                    width={width - 4}
                    height={fillHeight}
                    fill={finalColor}
                    clipPath='url(#occupancy-glass-clip)'
                />

                <line x1='3' y1={height / 3} x2={width - 3} y2={height / 3} stroke={STROKE_COLOR} strokeWidth='1' />
                <line x1='3' y1={(height / 3) * 2} x2={width - 3} y2={(height / 3) * 2} stroke={STROKE_COLOR} strokeWidth='1' />
            </svg>
        </Box>
    );
}

interface ZoningIndicatorProps {
    zoning?: string | null;
    size?: number;
    color?: string;
}

function getZoningIcon(zoning: string, size: number, color: string): React.ReactNode {
    const normalized = normalize(zoning);

    const icons: { [key: string]: React.ReactNode } = {
        agricultural: (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                {/* Tree trunk */}
                <rect x='28' y='32' width='8' height='16' fill={color} />
                {/* Tree crown - circle */}
                <circle cx='32' cy='24' r='16' fill={color} opacity='0.8' />
            </svg>
        ),
        commercial: (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Building outline */}
                <rect x='16' y='16' width='32' height='32' fill={color} opacity='0.8' />
                {/* Windows */}
                <rect x='20' y='20' width='6' height='6' fill='white' />
                <rect x='30' y='20' width='6' height='6' fill='white' />
                <rect x='40' y='20' width='6' height='6' fill='white' />
                <rect x='20' y='30' width='6' height='6' fill='white' />
                <rect x='30' y='30' width='6' height='6' fill='white' />
                <rect x='40' y='30' width='6' height='6' fill='white' />
            </svg>
        ),
        industrial: (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Main building */}
                <rect x='14' y='24' width='36' height='24' fill={color} opacity='0.7' />
                {/* Smokestacks */}
                <rect x='18' y='12' width='4' height='12' fill={color} />
                <rect x='30' y='10' width='4' height='14' fill={color} />
                <rect x='42' y='12' width='4' height='12' fill={color} />
                {/* Smoke puffs */}
                <circle cx='20' cy='8' r='2' fill={color} />
                <circle cx='32' cy='6' r='2' fill={color} />
                <circle cx='44' cy='8' r='2' fill={color} />
            </svg>
        ),
        institutional: (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Building */}
                <rect x='12' y='20' width='40' height='28' fill={color} opacity='0.7' />
                {/* Roof/triangle */}
                <polygon points='12,20 32,8 52,20' fill={color} opacity='0.8' />
                {/* Door */}
                <rect x='28' y='36' width='8' height='12' fill='white' />
            </svg>
        ),
        logistics: (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Warehouse */}
                <rect x='10' y='20' width='44' height='28' fill={color} opacity='0.7' />
                {/* Loading doors */}
                <rect x='16' y='24' width='8' height='12' fill='white' />
                <rect x='28' y='24' width='8' height='12' fill='white' />
                <rect x='40' y='24' width='8' height='12' fill='white' />
                {/* Truck outline */}
                <rect x='14' y='42' width='12' height='8' fill={color} opacity='0.6' />
                <circle cx='18' cy='50' r='2' fill={color} />
                <circle cx='24' cy='50' r='2' fill={color} />
            </svg>
        ),
        'mixed-use': (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Building 1 */}
                <rect x='12' y='20' width='16' height='28' fill={color} opacity='0.7' />
                {/* Building 2 */}
                <rect x='36' y='16' width='16' height='32' fill={color} opacity='0.8' />
            </svg>
        ),
        recreational: (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Ferris wheel circle */}
                <circle cx='32' cy='28' r='12' fill='none' stroke={color} />
                {/* Support frame */}
                <line x1='12' y1='42' x2='52' y2='42' stroke={color} strokeWidth='3' />
                {/* Spokes */}
                <line x1='20' y1='18' x2='20' y2='38' stroke={color} />
                <line x1='32' y1='16' x2='32' y2='40' stroke={color} />
                <line x1='44' y1='18' x2='44' y2='38' stroke={color} />
                {/* Pods */}
                <circle cx='32' cy='16' r='2' fill={color} />
                <circle cx='44' cy='24' r='2' fill={color} />
                <circle cx='32' cy='40' r='2' fill={color} />
                <circle cx='20' cy='24' r='2' fill={color} />
            </svg>
        ),
        residential: (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* House body */}
                <rect x='16' y='24' width='32' height='24' fill={color} opacity='0.7' />
                {/* Roof */}
                <polygon points='16,24 32,12 48,24' fill={color} opacity='0.8' />
                {/* Door */}
                <rect x='28' y='36' width='8' height='12' fill='white' />
                {/* Windows */}
                <rect x='20' y='28' width='6' height='6' fill='white' />
                <rect x='38' y='28' width='6' height='6' fill='white' />
            </svg>
        ),
        'retail shopping': (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Shopping bag outline */}
                <path d='M 18 28 L 20 16 L 44 16 L 46 28 L 18 28' fill={color} opacity='0.7' />
                {/* Handles */}
                <path d='M 26 16 Q 26 12 32 12 Q 38 12 38 16' stroke={color} strokeWidth='2' fill='none' />
                {/* Products inside */}
                <circle cx='28' cy='32' r='3' fill='white' />
                <circle cx='36' cy='32' r='3' fill='white' />
            </svg>
        ),
        'special purpose': (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Gear outline */}
                <circle cx='32' cy='32' r='10' fill={color} opacity='0.6' />
                <circle cx='32' cy='32' r='5' fill='white' />
                {/* Teeth */}
                {[0, 60, 120, 180, 240, 300].map((angle) => {
                    const rad = (angle * Math.PI) / 180;
                    const x1 = 32 + 10 * Math.cos(rad);
                    const y1 = 32 + 10 * Math.sin(rad);
                    const x2 = 32 + 16 * Math.cos(rad);
                    const y2 = 32 + 16 * Math.sin(rad);
                    return <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth='2' />;
                })}
            </svg>
        ),
        'water related': (
            <svg width={size} height={size} viewBox='0 0 64 64' fill='none' stroke={color} strokeWidth='2'>
                {/* Water waves */}
                <path d='M 12 32 Q 18 28 24 32 T 40 32 T 56 32' fill='none' stroke={color} />
                <path d='M 12 40 Q 18 36 24 40 T 40 40 T 56 40' fill='none' stroke={color} />
                <path d='M 12 48 Q 18 44 24 48 T 40 48 T 56 48' fill='none' stroke={color} />
            </svg>
        ),
    };

    return icons[normalized] || null;
}

export function ZoningIndicator({ zoning, size = 32, color }: ZoningIndicatorProps) {
    // Use provided color or determine from theme
    const theme = typeof window !== 'undefined' ? localStorage.getItem('theme') || 'light' : 'light';
    const finalColor = color || (theme === 'dark' ? '#c9a45c' : '#1e3a8a');

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: size,
                height: size,
            }}
            title={zoning || 'Unknown zoning'}
        >
            {zoning ? getZoningIcon(zoning, size, finalColor) : <Typography variant='caption'>N/A</Typography>}
        </Box>
    );
}

export function GraphComponents({
    status,
    occupancy,
    color,
    size = 32,
}: GraphComponentsProps) {
    // Use provided color or determine from theme
    const theme = typeof window !== 'undefined' ? localStorage.getItem('theme') || 'light' : 'light';
    const finalColor = color || (theme === 'dark' ? '#c9a45c' : '#1e3a8a');

    return (
        <Stack direction='row' spacing={1.5} alignItems='center'>
            <StatusCircleIndicator status={status} color={finalColor} size={size} />
            <OccupancyGlassIndicator occupancy={occupancy} color={finalColor} />
            <Typography variant='caption' color='text.secondary'>
                {status ?? 'No status'} / {occupancy ?? 'No occupancy'}
            </Typography>
        </Stack>
    );
}

export default GraphComponents;
